import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import L, { LatLng, LatLngBounds, latLngBounds } from 'leaflet';
import {
    MapContainer, TileLayer,
    Marker, Popup, FeatureGroup, useMap, Polyline
} from 'react-leaflet';
import { EditControl } from "react-leaflet-draw";
import { UserView, WorkplaceModel } from '../../../models/general';
import { UserActivityView, ShiftView, WialonShiftTrack, WialonHistoryStatus, LocationApprovalStatus } from "../../../models/attendance";
import { getUsersLocation, addWorkplace, updateWorkplace, getWorkplaces, removeWorkplace } from '../../../api/General/Company';
import { getClockings, getShiftTrack } from '../../../api/Attendance/attendance';
import { FetchUsers } from '../../../api/General/account';
// Import Leaflet CSS first
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
// @ts-ignore
import './index.css'
import { useTranslation } from 'react-i18next';
import { useSelector, shallowEqual } from 'react-redux';
import { getDurationFormatedAgo } from '../../../utils';
import { PageLayout } from '../../../components/ui/PageLayout';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Map as MapIcon, History as HistoryIcon, X as XIcon, MapPinOff } from 'lucide-react';

// Fix for default markers in React Leaflet
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create a default icon instance to ensure proper initialization
const defaultIcon = new L.Icon.Default();

import { getFullUrl } from '../../../utils/url';

function escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;')
              .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

interface IProps {
    userInView?: UserView;
    companyId?: number;
    editable?: boolean;
    userSpecific?: boolean;
    companySpecific?: boolean;
}

const MapView = (props: IProps) => {
    const { company, userId: reduxUserId, rtl, locale } =
        useSelector((state: any) => ({
            company: state.user.company,
            userId: state.user.userId,
            rtl: state.user.rtl,
            locale: state.user.locale,
        }), shallowEqual);
    const POLL_INTERVAL_MS = 60 * 1000;
    const pollIntervalRef = useRef<any>(null);
    const mountedRef = useRef(false);
    //map: L.Map;
    // TimeAgo.addLocale(en)
    // TimeAgo.setDefaultLocale("en")
    const { t } = useTranslation();
    const [users, setUsers] = useState<UserActivityView[]>([]);
    const [usersLocations, setUsersLocations] = useState<any[]>([]);
    const [currentLocation, setCurrentLocation] = useState<LatLng>(new LatLng(24.7136, 46.6753));
    const [workplaces, setWorkplaces] = useState<(WorkplaceModel & { polygon?: L.Polygon })[]>([]);
    const [featureGroupVisible] = useState((props.userSpecific ?? false) || (props.companySpecific ?? false));
    const [zoom, setZoom] = useState(5);
    const [bounds, setBounds] = useState<LatLngBounds>(new LatLngBounds([0, 0], [0, 0]));
    const [lastZoomLevel, setLastZoomLevel] = useState<number | undefined>();
    const [lastCenter, setLastCenter] = useState<LatLng | undefined>();
    const [loading, setLoading] = useState(true);

    // History mode — load a completed shift's GPS track from Wialon.
    const [historyMode, setHistoryMode] = useState(false);
    const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
    const [historyDate, setHistoryDate] = useState<string>(todayIso);
    const [historyUserId, setHistoryUserId] = useState<string>('');
    const [historyUsers, setHistoryUsers] = useState<UserView[]>([]);
    const [historyShifts, setHistoryShifts] = useState<ShiftView[]>([]);
    const [historyShiftsLoading, setHistoryShiftsLoading] = useState(false);
    const [selectedHistoryShiftId, setSelectedHistoryShiftId] = useState<number | null>(null);
    const [track, setTrack] = useState<WialonShiftTrack | null>(null);
    const [trackLoading, setTrackLoading] = useState(false);
    const [trackError, setTrackError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        try {
            if (!mountedRef.current) return;
            setLoading(true);
            const companyId = company ? company.id : 0;
            const usersResp = await getUsersLocation(companyId, false);
            const fetchedUsers: UserActivityView[] = usersResp.payload || [];
            setUsers(fetchedUsers);

            const workplacesResp = await getWorkplaces(companyId, props.userInView?.id ?? "", props.userSpecific);
            setWorkplaces(workplacesResp.payload || []);

            const b: LatLng[] = [];
            const locs: any[] = [];
            for (let i = 0; i < fetchedUsers.length; i++) {
                const u = fetchedUsers[i];
                if (u.latitude !== undefined && u.longitude !== undefined) {
                    b.push(new L.LatLng(u.latitude, u.longitude));
                }
                locs.push({ userIndex: i, countryName: u.countryName ?? 'Unidentified Country' });
            }
            locs.sort((a, b2) => a.countryName.localeCompare(b2.countryName));
            setUsersLocations(locs);

            if (lastCenter && lastZoomLevel) {
                setZoom(lastZoomLevel);
                setCurrentLocation(lastCenter);
            } else if (b.length > 0) {
                setBounds(latLngBounds(b));
                navigator.geolocation.getCurrentPosition(pos => {
                    setCurrentLocation(new LatLng(pos.coords.latitude, pos.coords.longitude));
                }, () => { /* ignore */ });
            } else {
                navigator.geolocation.getCurrentPosition(pos => {
                    setCurrentLocation(new LatLng(pos.coords.latitude, pos.coords.longitude));
                }, () => { /* ignore */ });
            }

        } catch (e) {
            console.warn('Map data load failed', e);
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [company, props.companyId, props.userInView, reduxUserId, props.userSpecific, lastCenter, lastZoomLevel]);

    useEffect(() => {
        mountedRef.current = true;
        fetchData();

        // Live polling is paused while viewing historical tracks — the map is
        // showing past data and a live refresh would clobber the polyline.
        if (!historyMode) {
            pollIntervalRef.current = setInterval(() => {
                if (mountedRef.current) fetchData();
            }, POLL_INTERVAL_MS);
        }

        return () => {
            mountedRef.current = false;
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [fetchData, historyMode]);

    // Lazy-load users (filtered to those with a VehicleId) when entering history mode.
    useEffect(() => {
        if (!historyMode || historyUsers.length > 0) return;
        const companyId = company ? company.id : 0;
        if (!companyId) return;
        let cancelled = false;
        (async () => {
            try {
                const resp = await FetchUsers('Company', companyId, false);
                if (cancelled) return;
                const all = resp.payload || [];
                setHistoryUsers(all.filter(u => !!u.vehicleId));
            } catch (e) {
                console.warn('History users fetch failed', e);
            }
        })();
        return () => { cancelled = true; };
    }, [historyMode, historyUsers.length, company]);

    // Fetch shifts for the selected user+date.
    useEffect(() => {
        if (!historyMode || !historyUserId || !historyDate) {
            setHistoryShifts([]);
            return;
        }
        let cancelled = false;
        setHistoryShiftsLoading(true);
        (async () => {
            try {
                const resp = await getClockings(historyUserId, historyDate, historyDate);
                if (cancelled) return;
                const shifts = (resp.payload?.results || []).flatMap(d => d.shifts || []);
                setHistoryShifts(shifts);
            } catch (e) {
                console.warn('History shifts fetch failed', e);
                if (!cancelled) setHistoryShifts([]);
            } finally {
                if (!cancelled) setHistoryShiftsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [historyMode, historyUserId, historyDate]);

    const exitHistoryMode = useCallback(() => {
        setHistoryMode(false);
        setSelectedHistoryShiftId(null);
        setTrack(null);
        setTrackError(null);
    }, []);

    const loadShiftTrack = useCallback(async (shiftId: number) => {
        setSelectedHistoryShiftId(shiftId);
        setTrackLoading(true);
        setTrackError(null);
        setTrack(null);
        try {
            const resp = await getShiftTrack(shiftId);
            const payload = resp.payload;
            if (!payload) {
                setTrackError(t('Failed to load track'));
                return;
            }
            switch (payload.status) {
                case WialonHistoryStatus.Success:
                    setTrack(payload);
                    // Pan/zoom the map to the track. setBounds alone does nothing here
                    // because ChangeMapView reads currentLocation+zoom, not bounds — so
                    // compute the centroid + a zoom level that fits the extent.
                    if (payload.points.length > 0) {
                        const latlngs = payload.points.map(p => new L.LatLng(p.latitude, p.longitude));
                        const b = latLngBounds(latlngs);
                        setBounds(b);
                        const center = b.getCenter();
                        setCurrentLocation(center);
                        const sw = b.getSouthWest();
                        const ne = b.getNorthEast();
                        const span = Math.max(Math.abs(ne.lat - sw.lat), Math.abs(ne.lng - sw.lng));
                        // Rough lookup table — keeps the whole track in view.
                        let z = 15;
                        if (span > 0.005) z = 14;
                        if (span > 0.02) z = 13;
                        if (span > 0.05) z = 12;
                        if (span > 0.1) z = 11;
                        if (span > 0.25) z = 10;
                        if (span > 0.5) z = 9;
                        if (span > 1) z = 8;
                        if (span > 2) z = 7;
                        setZoom(z);
                    }
                    break;
                case WialonHistoryStatus.Disabled:
                    setTrackError(t('Wialon is not enabled for this company'));
                    break;
                case WialonHistoryStatus.VehicleNotAssigned:
                    setTrackError(t('No vehicle is assigned to this employee'));
                    break;
                case WialonHistoryStatus.VehicleNotFound:
                    setTrackError(t('The assigned vehicle was not found in Wialon'));
                    break;
                case WialonHistoryStatus.TokenLacksAccess:
                    setTrackError(t('The Wialon token does not have access to historical data. Ask the administrator to grant the execute reports permission.'));
                    break;
                case WialonHistoryStatus.NoData:
                    setTrackError(t('No GPS data was recorded for this shift'));
                    break;
                default:
                    setTrackError(t('Failed to load track'));
                    break;
            }
        } catch (e) {
            console.warn('Shift track fetch failed', e);
            setTrackError(t('Failed to load track'));
        } finally {
            setTrackLoading(false);
        }
    }, [t]);

    const trackLatLngs = useMemo<[number, number][]>(() => {
        if (!track || !track.points) return [];
        return track.points.map(p => [p.latitude, p.longitude]);
    }, [track]);

    // Use useCallback to recreate this function when the users state changes
    // (moved earlier)

    const ChangeMapView = ({ lat, lng, zoom }: any) => {
        const map = useMap();
        useEffect(() => {
            map.setView([lat, lng], zoom);
        }, [lat, lng, zoom]);
        return null;
    }

    const onCreated = async (e: any) => {
        const latlngs = (e.layer as any)._latlngs[0];
        await addWorkplace(props.userInView?.company ? (props.userInView.company?.id) : props.companyId ?? 0,
            props.userInView?.id ?? "", {
            bounds: latlngs,
            id: 0
        }).then(() => {
            fetchData();
        });
    }

    // Debounced edit handling to avoid multiple triggers / duplicate listeners
    const editingRef = useRef(false);
    const editRefreshTimeoutRef = useRef<number | null>(null);
    useEffect(() => () => { if (editRefreshTimeoutRef.current) { clearTimeout(editRefreshTimeoutRef.current); } }, []);
    const onEdited = useCallback(async (e: any) => {
        if (editingRef.current) return; // prevent overlapping executions
        editingRef.current = true;
        try {
            const layers = e.layers.getLayers();
            const updates: Promise<any>[] = [];
            for (let i = 0; i < layers.length; i++) {
                const layer = layers[i] as unknown as { id: number, _latlngs: LatLng[][] };
                if (layer.id && layer._latlngs?.[0]) {
                    updates.push(updateWorkplace(layer.id, { bounds: layer._latlngs[0], id: layer.id }));
                }
            }
            if (updates.length) {
                await Promise.allSettled(updates);
            }
        } finally {
            editingRef.current = false;
            if (editRefreshTimeoutRef.current) clearTimeout(editRefreshTimeoutRef.current);
            // Debounce fetch to let Leaflet finish any internal state changes
            editRefreshTimeoutRef.current = window.setTimeout(() => {
                fetchData();
            }, 300);
        }
    }, [fetchData]);
    const deletingRef = useRef(false);
    const onDeleted = useCallback(async (e: any) => {
        if (deletingRef.current) return;
        deletingRef.current = true;
        try {
            const layers = e.layers.getLayers();
            const ids: number[] = [];
            for (let i = 0; i < layers.length; i++) {
                const layer = layers[i] as unknown as { id?: number };
                if (layer.id) ids.push(layer.id);
            }
            if (ids.length) {
                await Promise.all(ids.map(id => removeWorkplace(id)));
            }
            fetchData();
        } finally {
            deletingRef.current = false;
        }
    }, [fetchData]);
    const onUserClick = (user: any) => {
        setBounds(new LatLngBounds([0, 0], [0, 0]));
        setZoom(18);
        setCurrentLocation(new LatLng(user.latitude, user.longitude));
    };
    const onCountryClick = (user: any) => {
        setBounds(new LatLngBounds([0, 0], [0, 0]));
        setZoom(8);
        setCurrentLocation(new L.LatLng(user.latitude, user.longitude));
    };

    // Derived data: group users by country for side panel
    const usersByCountry = useMemo(() => {
        const map = new Map<string, UserActivityView[]>();
        users.forEach(u => {
            const key = u.countryName || 'Unidentified Country';
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(u);
        });
        return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [users]);


    // FeatureGroup ref & effect to manage polygons without duplicating layers
    const featureGroupRef = useRef<L.FeatureGroup<any>>(null);
    useEffect(() => {
        if (!featureGroupRef.current) return;
        const fg = featureGroupRef.current;
        try {
            fg.clearLayers();
            workplaces.forEach(w => {
                if (w.bounds && w.bounds.length) {
                    const poly = L.polygon(w.bounds);
                    (poly as any).id = w.id;
                    w.polygon = poly; // keep reference if needed elsewhere
                    poly.addTo(fg);
                }
            });
        } catch (err) {
            console.warn('Error updating polygons', err);
        }
    }, [workplaces]);

    return (
        <PageLayout
            title={t('Maps')}
            description={t('View employee locations on the map')}
            icon={MapIcon}
            accentColor="blue"
            maxWidth="full"
        >
        <div className="relative w-full h-[calc(100vh-200px)] flex">
            {/* Side panel (countries & users / history) */}
            {!props.userSpecific && <div className="absolute top-4 end-4 z-[1200] w-[calc(100%-32px)] sm:w-[320px] max-h-[70vh] overflow-auto p-4 rounded-xl bg-card/95 backdrop-blur-sm border border-border shadow-lg">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-semibold text-foreground">
                        {historyMode ? t('Vehicle History') : t('Employees')}
                    </h3>
                    <div className="flex items-center gap-2">
                        {(loading || trackLoading || historyShiftsLoading) && <div className="h-[18px] w-[18px] border-2 border-primary border-t-transparent rounded-full animate-spin" />}
                        <button
                            type="button"
                            onClick={() => historyMode ? exitHistoryMode() : setHistoryMode(true)}
                            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-border hover:bg-muted transition-colors"
                            title={historyMode ? t('Back to live view') : t('View vehicle history')}
                        >
                            {historyMode ? <><XIcon className="h-3.5 w-3.5" />{t('Live')}</> : <><HistoryIcon className="h-3.5 w-3.5" />{t('History')}</>}
                        </button>
                    </div>
                </div>
                <hr className="border-border mb-2" />
                {!historyMode && (
                    <ul className="space-y-1">
                        {usersByCountry.map(([country, list]) => (
                            <li key={country} className="mb-1">
                                <button onClick={() => list[0] && onCountryClick(list[0])} className="w-full text-start px-2 py-1.5 rounded hover:bg-muted transition-colors">
                                    <div className="text-sm font-semibold text-foreground">{country}</div>
                                    <div className="text-xs text-muted-foreground">{`${list.length} ${t('users')}`}</div>
                                </button>
                                <div className="px-2 space-y-0.5">
                                    {list.map(u => (
                                        <button key={u.id} onClick={() => onUserClick(u)} className="w-full text-start px-2 py-1.5 rounded hover:bg-muted transition-colors">
                                            <div className="flex items-center gap-2 w-full">
                                                <img src={getFullUrl(u.imageUrl) || '/images/defaultProfile.png'} alt={u.fullName || 'User'} className="h-7 w-7 rounded-full object-cover" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm text-foreground truncate">{u.fullName || t('Unknown')}</div>
                                                    <div className="text-xs text-muted-foreground truncate">{u.lastActivity || t('No activity')}</div>
                                                </div>
                                                {u.workingDuration && <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground">{u.workingDuration.split('.')[0]}</span>}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </li>
                        ))}
                        {users.length === 0 && !loading && <li className="text-sm text-muted-foreground px-2">{t('No users')}</li>}
                    </ul>
                )}
                {historyMode && (
                    <div className="space-y-3">
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground">{t('Employee')}</label>
                            <select
                                value={historyUserId}
                                onChange={e => { setHistoryUserId(e.target.value); setSelectedHistoryShiftId(null); setTrack(null); setTrackError(null); }}
                                className="w-full px-2 py-1.5 rounded border border-border bg-background text-sm"
                            >
                                <option value="">{t('Select an employee')}</option>
                                {historyUsers.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.fullName || u.email} {u.vehicleId ? `— ${u.vehicleId}` : ''}
                                    </option>
                                ))}
                            </select>
                            {historyUsers.length === 0 && (
                                <p className="text-[11px] text-muted-foreground">{t('No employees have a vehicle assigned')}</p>
                            )}
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground">{t('Date')}</label>
                            <input
                                type="date"
                                value={historyDate}
                                max={todayIso}
                                onChange={e => { setHistoryDate(e.target.value); setSelectedHistoryShiftId(null); setTrack(null); setTrackError(null); }}
                                className="w-full px-2 py-1.5 rounded border border-border bg-background text-sm"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">{t('Shifts')}</label>
                            {!historyUserId && (
                                <p className="text-[11px] text-muted-foreground mt-1">{t('Select an employee to see their shifts')}</p>
                            )}
                            {historyUserId && historyShifts.length === 0 && !historyShiftsLoading && (
                                <p className="text-[11px] text-muted-foreground mt-1">{t('No shifts on this date')}</p>
                            )}
                            <ul className="mt-1 space-y-1">
                                {historyShifts.map(s => {
                                    const start = new Date(s.clockInTime);
                                    const end = s.clockOutTime ? new Date(s.clockOutTime) : null;
                                    const isSelected = selectedHistoryShiftId === s.id;
                                    const outOfBounds = s.clockInWasOutOfBounds || s.clockOutWasOutOfBounds;
                                    const oobMeters = s.clockInWasOutOfBounds
                                        ? s.clockInOutOfBoundsDistanceMeters
                                        : s.clockOutOutOfBoundsDistanceMeters;
                                    const oobReason = s.clockInWasOutOfBounds
                                        ? s.clockInOutOfBoundsReason
                                        : s.clockOutOutOfBoundsReason;
                                    // KEYS stay colon-free (i18n.js nsSeparator ':'); the joined string is a plain title attr.
                                    const oobTitleParts: string[] = [
                                        oobMeters != null
                                            ? t('Clocked {{meters}}m outside the allowed area', { meters: oobMeters })
                                            : t('Clocked outside the allowed area'),
                                    ];
                                    if (oobReason) oobTitleParts.push(t('Reason {{reason}}', { reason: oobReason }));
                                    // RequireApproval review state — either clock side may be pending/rejected.
                                    const approvalRejected = s.clockInLocationApprovalStatus === LocationApprovalStatus.Rejected
                                        || s.clockOutLocationApprovalStatus === LocationApprovalStatus.Rejected;
                                    const approvalPending = !approvalRejected
                                        && (s.clockInLocationApprovalStatus === LocationApprovalStatus.Pending
                                            || s.clockOutLocationApprovalStatus === LocationApprovalStatus.Pending);
                                    const approvalNote = s.clockInLocationApprovalStatus === LocationApprovalStatus.Rejected
                                        ? s.clockInLocationDecisionNote
                                        : s.clockOutLocationDecisionNote;
                                    return (
                                        <li key={s.id}>
                                            <button
                                                type="button"
                                                onClick={() => loadShiftTrack(s.id)}
                                                className={`w-full text-start px-2 py-1.5 rounded transition-colors text-xs ${isSelected ? 'bg-primary/10 border border-primary' : 'border border-border hover:bg-muted'}`}
                                            >
                                                <div className="font-medium text-foreground">
                                                    {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    {' → '}
                                                    {end ? end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : t('Still open')}
                                                </div>
                                                {s.workingDuration && (
                                                    <div className="text-[11px] text-muted-foreground">
                                                        {t('Working Duration-')}{s.workingDuration.split('.')[0]}
                                                    </div>
                                                )}
                                                {outOfBounds && (
                                                    <div className="mt-1 flex flex-wrap items-center gap-1">
                                                        <StatusBadge
                                                            variant="warning"
                                                            size="sm"
                                                            icon={MapPinOff}
                                                            title={oobTitleParts.join(' — ')}
                                                        >
                                                            {t('Out of bounds')}
                                                        </StatusBadge>
                                                        {approvalPending && (
                                                            <StatusBadge
                                                                variant="warning"
                                                                size="sm"
                                                                title={t('Awaiting approval for clocking outside the allowed area')}
                                                            >
                                                                {t('Pending approval')}
                                                            </StatusBadge>
                                                        )}
                                                        {approvalRejected && (
                                                            <StatusBadge variant="error" size="sm" title={approvalNote || undefined}>
                                                                {t('Rejected')}
                                                            </StatusBadge>
                                                        )}
                                                    </div>
                                                )}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                        {trackError && (
                            <div className="text-xs p-2 rounded border border-destructive/40 bg-destructive/10 text-destructive">
                                {trackError}
                            </div>
                        )}
                        {track && track.summary && (
                            <div className="text-xs p-2 rounded border border-border bg-muted/40 space-y-1">
                                <div className="font-semibold text-foreground">{track.unitName}</div>
                                <div className="flex justify-between"><span className="text-muted-foreground">{t('Distance')}</span><span>{track.summary.distanceKm} {t('km')}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">{t('Max speed')}</span><span>{track.summary.maxSpeed} {t('km/h')}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">{t('Avg speed')}</span><span>{track.summary.avgSpeed} {t('km/h')}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">{t('Stops')}</span><span>{track.summary.stopCount}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">{t('Moving')}</span><span>{track.summary.movingTime?.split('.')[0]}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">{t('Idle')}</span><span>{track.summary.idleTime?.split('.')[0]}</span></div>
                            </div>
                        )}
                    </div>
                )}
            </div>}
            <MapContainer
                key={'mapppppp'}
                style={{ height: '100%', width: '100%' }}
                center={currentLocation}
                zoom={zoom}
                scrollWheelZoom
                attributionControl={false}
            >
            <TileLayer url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" subdomains={['mt0', 'mt1', 'mt2', 'mt3']} />
            <ChangeMapView lat={currentLocation.lat} lng={currentLocation.lng} zoom={zoom} />
            {historyMode && track && trackLatLngs.length > 0 && (
                <>
                    <Polyline positions={trackLatLngs} pathOptions={{ color: '#2563eb', weight: 4, opacity: 0.85 }} />
                    <Marker position={trackLatLngs[0]}>
                        <Popup>{t('Shift start')}<br />{new Date(track.points[0].timeUtc).toLocaleString()}</Popup>
                    </Marker>
                    <Marker position={trackLatLngs[trackLatLngs.length - 1]}>
                        <Popup>{t('Shift end')}<br />{new Date(track.points[track.points.length - 1].timeUtc).toLocaleString()}</Popup>
                    </Marker>
                </>
            )}
            {!historyMode && !props.userSpecific && (<>
                {users && users.length > 0 && users.map(x => {
                    // Validate user data before rendering
                    if (!x || !x.id || x.latitude === undefined || x.longitude === undefined) {
                        console.warn('Invalid user data:', x);
                        return null;
                    }

                    // Create custom icon or use default with proper error handling
                    let markerIcon;
                    try {
                        if (x.wialonUnitName) {
                            markerIcon = new L.DivIcon({
                                className: 'wialon-marker',
                                html: `
                                    <div class="wialon-marker-container">
                                        <div class="wialon-marker-label">
                                            <span class="wialon-employee-name">${escapeHtml(x.fullName || '')}</span>
                                            <span class="wialon-unit-name">${escapeHtml(x.wialonUnitName || '')}</span>
                                        </div>
                                        <div class="wialon-marker-icon">
                                            ${x.iconUrl
                                                ? `<img src="${encodeURI(x.iconUrl)}" alt="vehicle" width="32" height="32" />`
                                                : '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.8C1.4 11.3 1 12.1 1 13v3c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>'}
                                        </div>
                                    </div>
                                `,
                                iconSize: [120, 70],
                                iconAnchor: [60, 70],
                                popupAnchor: [0, -70],
                            });
                        } else if (x.iconUrl) {
                            markerIcon = new L.Icon({
                                iconUrl: x.iconUrl,
                                iconSize: [25, 41],
                                iconAnchor: [12, 41],
                                popupAnchor: [1, -34],
                                shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
                            });
                        } else {
                            markerIcon = defaultIcon;
                        }
                    } catch (error) {
                        console.warn('Error creating marker icon, using default:', error);
                        markerIcon = defaultIcon;
                    }
                    return (
                        <Marker key={x.id} position={[x.latitude, x.longitude]} icon={markerIcon} >
                            <Popup>
                                <div className="flex flex-col items-center text-center" style={{ minWidth: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                    <img src={getFullUrl(x.imageUrl) || '/images/defaultProfile.png'} alt={x.fullName || 'User'} className="h-14 w-14 rounded-full object-cover mb-2" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', marginBottom: 8 }} />
                                    <a href={`/attendance/overview/${x.id}/${encodeURIComponent(x.fullName || '')}`} className="font-semibold text-primary hover:underline" style={{ fontWeight: 600 }}>
                                        {x.fullName || t('Unknown User')}
                                    </a>
                                    <span className="text-xs text-muted-foreground mt-1" style={{ fontSize: '0.75rem', marginTop: 4 }}>
                                        {t(x.lastActivity || 'Unknown')} - {locale === 'ar' ? t('ago') : ''} {getDurationFormatedAgo(x.lastActivityDuration || '0')} {locale === 'ar' ? '' : t('ago')}
                                    </span>
                                    {x.workingDuration && (
                                        <span className="mt-2 px-2 py-0.5 rounded-full text-xs font-medium bg-muted" style={{ marginTop: 8, padding: '2px 8px', borderRadius: 9999, fontSize: '0.75rem', fontWeight: 500 }}>
                                            {`${t('Working Duration-')}${(x.workingDuration.split('.').length <= 2 ? x.workingDuration.split('.')[0] : x.workingDuration.split('.')[0] + ' ' + t('days') + ',' + x.workingDuration.split('.')[1])}`}
                                        </span>
                                    )}
                                    {x.wialonLastSeen && (
                                        <span className="text-xs text-muted-foreground mt-1" style={{ fontSize: '0.75rem', marginTop: 4 }}>
                                            {t('GPS')}: {new Date(x.wialonLastSeen).toLocaleTimeString()}
                                        </span>
                                    )}
                                </div>
                            </Popup>
                        </Marker>
                    );
                }).filter(Boolean)}
            </>)
            }
            {featureGroupVisible ? (
                <FeatureGroup ref={featureGroupRef}>
                    {props.editable || props.editable === undefined ?
                        <EditControl
                            position="topleft"
                            onEdited={onEdited}
                            onCreated={onCreated}
                            onDeleted={onDeleted}
                            draw={{
                                rectangle: false,
                                polyline: false,
                                circle: false,
                                marker: false,
                                circlemarker: false,
                                polygon: {
                                    allowIntersection: false,
                                }
                            }}
                        />
                        : null
                    }
                </FeatureGroup>
            ) : null
            }
            </MapContainer>
        </div>
        </PageLayout>
    );
};

export default MapView;

